/**
 * The Report Registry — the TypeScript mirror of
 * Lighthouse.Api Contracts/Models/ObservatoryRegistryModels.cs, served by
 * `GET /api/observatory/registry` (ObservatoryRegistryController, LighthouseAdmin
 * at class level, GET only).
 *
 * camelCase, as the API serialises. Every enum arrives as its C# NAME (the API has
 * no global enum converter; the controller serves `ToString()`), and every C#
 * nullable is `| null` — the API writes nulls explicitly, so a missing value is
 * `null` on the wire, never `undefined`. A null renders NOT_MEASURED
 * (utils/format.ts), never 0 or a reassuring default.
 *
 * An inventory, not a data surface: no report definition, field list or data
 * source is served, by decision (doc 17 §4, amendment A2).
 *
 * TIMESTAMPS: `createdAt`, `modifiedAt` and `lastRunAt` are C# `DateTime`, not
 * `DateTimeOffset`. They are written as UTC, but SQL Server hands them back with no
 * kind, so the wire value can carry NO offset suffix (`2026-09-01T12:00:00`), and
 * `Date.parse` reads an offset-less ISO string as LOCAL time. Treat them as UTC.
 *
 * This module imports nothing, so it stays safe to import under vitest.
 */

/** ReportStatus, by name, in the server's declaration order. */
export type RegistryReportStatus =
  | 'Active'
  | 'AttentionRequired'
  | 'Delisted'
  | 'Archived'
  | 'Retired'
  | 'Deleted'

/** Every status, in the server's (declaration) order. */
export const REGISTRY_STATUSES: readonly RegistryReportStatus[] = [
  'Active', 'AttentionRequired', 'Delisted', 'Archived', 'Retired', 'Deleted',
]

/**
 * The `status` query value, which is the lower-cased name. Anything else is a 400
 * ("Unknown status") rather than an ignored filter.
 */
export type RegistryStatusFilter =
  | 'active'
  | 'attentionrequired'
  | 'delisted'
  | 'archived'
  | 'retired'
  | 'deleted'

/** Each status's query value, spelled out so the pairing is checked by the compiler. */
export const REGISTRY_STATUS_FILTER: Readonly<Record<RegistryReportStatus, RegistryStatusFilter>> = {
  Active: 'active',
  AttentionRequired: 'attentionrequired',
  Delisted: 'delisted',
  Archived: 'archived',
  Retired: 'retired',
  Deleted: 'deleted',
}

/** PrincipalType, by name: what kind of principal `ownerUserId` names. */
export type RegistryOwnerPrincipalType = 'User' | 'Group' | 'Role'

/** One report that exists, and where a platform operator would find it. */
export interface RegistryReport {
  /** Deep-links to Cartographer, which owns every action on the report. */
  reportId: string
  name: string
  /** The author's own description, or null when they wrote none. */
  description: string | null
  /**
   * The lifecycle state. Every status is listed. Three cannot occur in practice
   * today: Retired and Deleted have no writer, and Delisted's writers are
   * unreachable (the verb has no caller, the automatic path is locked in code). So
   * "0 delisted" describes the codebase, not the estate.
   */
  status: RegistryReportStatus
  /**
   * The workspace the report is filed in. NULL IS HOW A PERSONAL REPORT IS STORED,
   * so render a null as "Personal (not filed)", never as shared and never as a
   * dash.
   */
  workspaceId: string | null
  /** Null when the report is filed in no workspace. */
  workspaceName: string | null
  /**
   * The report's workspace row is of kind Personal. False on almost every personal
   * report, because a personal report has no workspace (see `workspaceId`), so it
   * must not be read alone.
   */
  isPersonalWorkspace: boolean
  /**
   * The accountable principal's opaque id: the business owner, falling back to the
   * author. Not always a user's id, whatever the name says; `ownerPrincipalType`
   * says which. Orphanhood is `status === 'AttentionRequired'`, never a null here.
   */
  ownerUserId: string | null
  /**
   * What `ownerUserId` names, read the way Cartographer's gate reads it. Null
   * exactly when `ownerUserId` is null.
   *
   * A Group or Role owner makes NOBODY the owner: the gate matches the owner only as
   * a single user and does not expand a group. Do not render one as an unnamed
   * person.
   */
  ownerPrincipalType: RegistryOwnerPrincipalType | null
  /**
   * The name stamped at creation, served only while the owner is still the creator.
   * Null after any transfer, when never stamped, and when there is no owner id.
   * Never resolved from a directory.
   */
  ownerDisplayName: string | null
  /** UTC, possibly with no offset suffix (see the module note). */
  createdAt: string
  /** Null when the report has never been edited, which is a genuine null. */
  modifiedAt: string | null
  /** Null when the report has never run. From SavedReport.LastRunAt, not the trimmed run log. */
  lastRunAt: string | null
  /**
   * ReportVisibility by name ('Private' or 'Organization'). Unenforced: read
   * `visibilityIsEnforced` before rendering it, and never colour or filter on it
   * while that is false.
   */
  visibility: string
  /**
   * Schedules marked active — a measured zero where there are none. Rows, not
   * deliveries: it omits the runnable clause, so on a report that cannot run
   * (`isRunnable` false) every one of these is inert.
   */
  activeScheduleCount: number
  /**
   * Live explicit grants to a User — each one a person. Zero means "no explicit
   * grants", not "nobody can reach it". (Was `permissionCount`, every principal type.)
   */
  userGrantCount: number
  /**
   * Live explicit grants to a Group or Role — group members not resolvable. They
   * confer nothing on anyone; zero by construction while only Cartographer writes.
   */
  groupOrRoleGrantCount: number
  /**
   * Whether the report's status still runs (Active, AttentionRequired, Delisted),
   * decided by the server from the shared rule. When false the counts on the row are
   * view-only (grants) or inert (schedules).
   */
  isRunnable: boolean
}

/** GET /api/observatory/registry */
export interface RegistryOverview {
  /** Ordered by name, then id. */
  reports: RegistryReport[]
  /** 1-based. */
  pageNumber: number
  /** After the server's 1..200 clamp. */
  pageSize: number
  totalPages: number
  /** Reports matching the filters, across every page. */
  totalCount: number
  /** Every report in the estate, ignoring the filters: the denominator. */
  reportsInRegistry: number
  /** Whether `visibility` means anything. False today. */
  visibilityIsEnforced: boolean
}

/**
 * What the registry is asked. A filter left unset, or set to a blank string, is
 * not sent: a blank asks no question.
 */
export interface RegistryQuery {
  /** Negative values are treated as zero by the server. */
  skip?: number
  /** Server-clamped to 1..200; the default is 50. */
  top?: number
  /** A workspace id that matches no workspace is a 400, not an empty page. */
  workspaceId?: string | null
  status?: RegistryStatusFilter | null
  /** An exact principal id, matched against the same key the rows serve. Not a name. */
  owner?: string | null
  /** Case-insensitive substring over name and description only. */
  search?: string | null
}
